/* eslint-disable max-len */
import React, { ReactNode } from 'react'
import { InferType } from 'yup'
import { captureException } from '@sentry/react'
import { useMutation } from '@tanstack/react-query'
import { useAppTranslation } from '../../../../../../hooks/useAppTranslation'
import { useToastNotifications } from '../../../../../../hooks/useToastNotification'
import { ToastType } from '../../../../../common/Toast/toast-type'
import { Modal } from '../../../../../common/Modal'
import { ModalHeader } from '../../../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../../../common/Modal/ModalBody'
import { CreateUpdateSocietyWebsiteFAQSectionForm } from '../CreateUpdateSocietyWebsiteFAQSectionForm'
import { NSocietiesWebsites } from '../../../../../../interfaces/services/societies-websites.interfaces'
import { societyWebsiteFAQSectionCreateSchema } from '../../../../../../forms/schemas/society_website_faq_section_create'
import { ISocietyWebsiteModel } from '../../../../../../interfaces/models/societies-websites.interfaces'
import { patchWebsite } from '../../../../../../api/society-websites'

interface CreateUpdateSocietyWebsiteFAQSectionModalProps {
  website: ISocietyWebsiteModel
  show: boolean
  close: () => void
  children?: React.ReactNode
  sectionId?: string
}

export const CreateUpdateSocietyWebsiteFAQSectionModal = ({
  website,
  sectionId,
  children,
  show,
  close,
}: CreateUpdateSocietyWebsiteFAQSectionModalProps): ReactNode => {
  const { translate } = useAppTranslation()
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!sectionId

  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateSectionFailure'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createSectionFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateQuestionsAndAnswers.flashMessage.updateSectionSuccess'
      : 'createUpdateQuestionsAndAnswers.flashMessage.createSectionSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }

  const getUpdatedSection = (
    data: InferType<typeof societyWebsiteFAQSectionCreateSchema>
  ): NSocietiesWebsites.NPatch.IRequestBody => {
    return {
      brokerQuestion: {
        ...website.brokerQuestion,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        sections: website.brokerQuestion?.sections?.map((_section) => {
          if (_section._id === sectionId) {
            return {
              ..._section,
              title: data.title,
            }
          }
          return _section
        }),
      },
    }
  }

  const getNewSection = (
    data: InferType<typeof societyWebsiteFAQSectionCreateSchema>
  ): NSocietiesWebsites.NPatch.IRequestBody => {
    return {
      brokerQuestion: {
        ...website.brokerQuestion,
        sections: [
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ...(website.brokerQuestion?.sections
            ? website.brokerQuestion.sections
            : []),
          {
            title: data.title,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            questions: [],
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            order: website.brokerQuestion?.sections
              ? website.brokerQuestion.sections.length
              : 0,
          },
        ],
      },
    }
  }

  const { mutate: mutatePatchWebsite, isPending: isUpdatingWebsite } =
    useMutation({
      mutationFn: patchWebsite,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyWebsiteFAQSectionCreateSchema>
  ): Promise<void> => {
    if (updateMode) {
      const formData = getUpdatedSection(data)
      mutatePatchWebsite({ id: website._id, body: formData })
    } else {
      const formData = getNewSection(data)
      mutatePatchWebsite({ id: website._id, body: formData })
    }
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isUpdatingWebsite

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateQuestionsAndAnswers.editSectionTitle'
                  : 'createUpdateQuestionsAndAnswers.addNewSectionTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              <CreateUpdateSocietyWebsiteFAQSectionForm
                website={website}
                onError={onError}
                onSubmit={onSubmit}
                onClose={close}
                loading={loading}
                sectionId={sectionId}
              />
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
